// =====================================================================
// Scheduling — time-based triggers that fire an action or sequence.
// Spec: docs/superpowers/specs/2026-06-23-scheduling-design.md
// =====================================================================
// Structured recurrence (no cron-string dependency) + a pure, timezone-aware
// `computeNextFire`. days_of_week uses 0=Sunday..6=Saturday (JS getDay()).
// =====================================================================

import { z } from 'zod';

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'time_of_day must be HH:MM (24h)');

export const RecurrenceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('interval'), every_minutes: z.number().int().min(1) }),
  z.object({ kind: z.literal('daily'), time_of_day: HHMM, timezone: z.string().min(1) }),
  z.object({
    kind: z.literal('weekly'),
    time_of_day: HHMM,
    timezone: z.string().min(1),
    days_of_week: z.array(z.number().int().min(0).max(6)).min(1),
  }),
]);
export type ParsedRecurrence = z.infer<typeof RecurrenceSchema>;

export const ScheduleTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('sequence'),
    sequence_ref: z.string().min(1),
    param_overrides: z.record(z.string(), z.unknown()).default({}),
  }),
  z.object({
    kind: z.literal('action'),
    action_ref: z.string().min(1),
    param_overrides: z.record(z.string(), z.unknown()).default({}),
  }),
]);
export type ParsedScheduleTarget = z.infer<typeof ScheduleTargetSchema>;

export const ScheduleSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  name: z.string().min(1),
  enabled: z.boolean().default(true),
  recurrence: RecurrenceSchema,
  target: ScheduleTargetSchema,
  next_fire_ms: z.number().int().nullable().default(null),
  last_fired_ms: z.number().int().nullable().default(null),
  last_run_id: z.string().nullable().default(null),
  archived_at_ms: z.number().int().nullable().default(null),
  // Sub-project B / Task P2 — board-owned marker. The deploy path rewrites
  // this set on every successful deploy (stampBoardOwned): every deployable
  // schedule (isDeployableSchedule) is stamped board-owned; everything else
  // owned by that board is cleared. `tickDueSchedules` skips any schedule
  // with a marker present (presence-only check — no live-generation read,
  // avoiding a Firestore composite-index gate). System-managed: NOT on
  // CreateScheduleSchema/UpdateScheduleSchema.
  board_owned: z
    .object({ board_id: z.string(), generation: z.number().int() })
    .nullable()
    .default(null),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});
export type ParsedSchedule = z.infer<typeof ScheduleSchema>;

export const CreateScheduleSchema = z.object({
  name: z.string().min(1),
  enabled: z.boolean().optional(),
  recurrence: RecurrenceSchema,
  target: ScheduleTargetSchema,
});
export type ParsedCreateSchedule = z.infer<typeof CreateScheduleSchema>;

export const UpdateScheduleSchema = z.object({
  name: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
  recurrence: RecurrenceSchema.optional(),
  target: ScheduleTargetSchema.optional(),
});
export type ParsedUpdateSchedule = z.infer<typeof UpdateScheduleSchema>;

// ---------------------------------------------------------------------
// Next-fire computation (pure)
// ---------------------------------------------------------------------

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** Wall-clock fields of an instant in a given IANA timezone. */
function zonedParts(utcMs: number, timeZone: string) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    weekday: 'short',
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(new Date(utcMs))) p[part.type] = part.value;
  const hour = p.hour === '24' ? 0 : Number(p.hour);
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    hour,
    minute: Number(p.minute),
    second: Number(p.second),
  };
}

/** Offset (ms) such that utc + offset == the "naive UTC" of the local fields. */
export function tzOffsetMs(utcMs: number, timeZone: string): number {
  const f = zonedParts(utcMs, timeZone);
  const asUtc = Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second);
  return asUtc - utcMs;
}

/** UTC instant for a local wall time (Y-M-D H:M) in tz. Handles DST via a refine pass. */
function utcForLocal(
  y: number,
  m: number,
  d: number,
  hh: number,
  mm: number,
  timeZone: string,
): number {
  const naive = Date.UTC(y, m - 1, d, hh, mm, 0);
  let off = tzOffsetMs(naive, timeZone);
  let utc = naive - off;
  const off2 = tzOffsetMs(utc, timeZone);
  if (off2 !== off) {
    off = off2;
    utc = naive - off;
  }
  return utc;
}

/** Day-of-week (0=Sun..6=Sat) for an instant in tz. */
function zonedDow(utcMs: number, timeZone: string): number {
  const f = zonedParts(utcMs, timeZone);
  // Construct from the local Y-M-D at noon UTC to avoid edge wrap, read weekday.
  return new Date(Date.UTC(f.year, f.month - 1, f.day, 12)).getUTCDay();
}

function parseHHMM(s: string): { hh: number; mm: number } {
  const [h, m] = s.split(':');
  return { hh: Number(h), mm: Number(m) };
}

/**
 * The next fire time strictly after `fromMs`, or null if none applies.
 * - interval: epoch-aligned multiples of every_minutes.
 * - daily/weekly: next matching local wall-clock occurrence in the recurrence tz.
 */
export function computeNextFire(rec: ParsedRecurrence, fromMs: number): number {
  if (rec.kind === 'interval') {
    const step = rec.every_minutes * MINUTE_MS;
    return (Math.floor(fromMs / step) + 1) * step;
  }

  const { hh, mm } = parseHHMM(rec.time_of_day);
  const allowedDows = rec.kind === 'weekly' ? new Set(rec.days_of_week) : null;

  // Walk forward day-by-day (local) up to 8 days to cover any weekly gap + DST.
  for (let i = 0; i <= 8; i++) {
    const probe = fromMs + i * DAY_MS;
    const f = zonedParts(probe, rec.timezone);
    const candidate = utcForLocal(f.year, f.month, f.day, hh, mm, rec.timezone);
    if (candidate <= fromMs) continue;
    if (allowedDows && !allowedDows.has(zonedDow(candidate, rec.timezone))) continue;
    return candidate;
  }
  // Fallback (shouldn't happen): one day out.
  return fromMs + DAY_MS;
}
